import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

/**
 * Lightweight toast notifications.
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success('Saved!');
 *   toast.error('Something went wrong');
 */

const ToastContext = createContext(null);

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
  }, []);

  const show = useCallback((message, type = 'info', duration = 4000) => {
    const id = nextId++;
    setToasts(prev => [...prev.slice(-3), { id, message, type }]);
    timersRef.current[id] = setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  const value = {
    show,
    success: (msg, d) => show(msg, 'success', d),
    error:   (msg, d) => show(msg, 'error', d ?? 6000),
    info:    (msg, d) => show(msg, 'info', d),
  };

  const colors = {
    success: { bg: '#3d7a52', icon: '✓' },
    error:   { bg: '#b54b3f', icon: '!' },
    info:    { bg: '#1A6068', icon: 'i' },
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div style={{
        position: 'fixed', bottom: '24px', left: '50%',
        transform: 'translateX(-50%)', zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: '8px',
        alignItems: 'center', pointerEvents: 'none',
        width: 'min(92vw, 420px)',
      }}>
        {toasts.map(t => (
          <div
            key={t.id}
            onClick={() => dismiss(t.id)}
            style={{
              background: colors[t.type].bg, color: '#fff',
              padding: '12px 20px', borderRadius: '10px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
              display: 'flex', alignItems: 'center', gap: '10px',
              cursor: 'pointer', pointerEvents: 'auto',
              width: '100%', fontSize: '14px', lineHeight: 1.4,
              animation: 'toast-in 0.25s ease',
            }}
          >
            <span style={{
              width: '20px', height: '20px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.25)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: '12px', fontWeight: 'bold', flexShrink: 0,
            }}>
              {colors[t.type].icon}
            </span>
            {t.message}
          </div>
        ))}
      </div>
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  // Null-safe fallback so components outside the provider don't crash
  if (!ctx) {
    return { show: () => {}, success: () => {}, error: () => {}, info: () => {} };
  }
  return ctx;
}
